import { QuestionPropertyIds } from 'data/BackendEntityConstants';
import { CreateRFXQuestionAttachmentOutput } from 'gql';
import { Document, Property, RFXDocumentType, RFXRequest, RFXRevisionedId } from 'interfaces/RFXBackendModel';
import { RFXQuestion, RFXUserType } from 'interfaces/RFXQuestionModel';
import _ from 'lodash';
import {
  VendorParticipationStatus,
  VendorStatus
} from 'rfxportal/pages/ViewPages/BuyerRFXDetailView/components/SuppliersCorrespondence/types';
import { v4 as uuid } from 'uuid';

import { createProperty, extractPropertyOrElseDefault, getS3UriFromS3Arn } from './RFXUtils';

/**
 * Utility function to check if a questionYou confirm is a participation confirmation
 */
export const isParticipationConfirmation = (rfxQuestion: RFXQuestion): boolean => {
  return Boolean(extractPropertyOrElseDefault(rfxQuestion.properties, 'ParticipationConfirmation', ''));
};

/**
 * Get participation confirmation from a message (RFXQuestion entity)
 * Return its value ("true" or "false") as string.
 * If the RFXQuestion entity is not participatio confirmation, it will return empty string.
 */
export const getParticipationConfirmation = (rfxQuestion: RFXQuestion): boolean => {
  return extractPropertyOrElseDefault(rfxQuestion.properties, 'ParticipationConfirmation', '').toLowerCase() === 'true';
};

/**
 * Get sender name of a message (RFXQuestion entity).
 * If internal user then simply return "AWS",
 * otherwise it would be the vendorId.
 */
export const getAuthor = (rfxQuestion: RFXQuestion): string => {
  if (rfxQuestion.author.userType === 'INTERNAL') return 'AWS';
  return rfxQuestion.author.id.value;
};

/**
 * Creates a RFI question for a new message
 */
export function createBuyerRFIQuestion({
  rfxRequestId,
  username,
  message,
  text,
  vendorId
}: {
  rfxRequestId: string;
  username: string;
  message: string;
  text: string;
  vendorId: string;
}): RFXQuestion {
  return {
    rfxQuestionRevisionedId: {
      id: {
        value: `${rfxRequestId}-${Date.now()}-${username}`
      },
      revision: 1
    },
    rfxQuestionVersion: 1,
    rfxRequestRevisionedId: {
      id: { value: rfxRequestId },
      revision: 1
    },
    author: {
      userType: RFXUserType.INTERNAL,
      id: {
        value: username
      }
    },
    rfxQuestionClassId: {
      value: 'AWSRFIQuestion'
    },
    message,
    contentType: 'text/html',
    createdAt: Date.now(),
    lastUpdatedAt: Date.now(),
    rfxQuestionDocuments: [],
    properties: [
      createProperty(QuestionPropertyIds.MessageTo, vendorId),
      createProperty(QuestionPropertyIds.MessageContent, text)
    ]
  };
}

/**
 * Create supplier RFXQuestion
 */
export function createSupplierRFXQuestion({
  rfxRequestRevisionedId,
  vendorId,
  message,
  properties,
  contentType = 'text/plain'
}: {
  rfxRequestRevisionedId: RFXRevisionedId;
  vendorId: string;
  message: string;
  properties?: Property[];
  contentType?: string;
}): RFXQuestion {
  return {
    rfxQuestionRevisionedId: {
      id: {
        value: `${rfxRequestRevisionedId.id.value}-${Date.now()}-${vendorId}`
      },
      revision: rfxRequestRevisionedId.revision
    },
    rfxQuestionVersion: 1,
    rfxRequestRevisionedId: rfxRequestRevisionedId,
    author: {
      userType: RFXUserType.EXTERNAL,
      id: {
        value: vendorId
      }
    },
    rfxQuestionClassId: {
      value: 'AWSRFQQuestion'
    },
    message,
    contentType,
    createdAt: Date.now(),
    lastUpdatedAt: Date.now(),
    rfxQuestionDocuments: [],
    properties: properties || []
  };
}

/**
 * Create supplier RFI question
 *
 * The question message is the html from rich text editor.
 * The pure text content is saved in "MessageContent" properties.
 */
export function createSupplierRFIQuestion({
  rfxRequestId,
  vendorId,
  message,
  text,
  properties,
  contentType = 'text/html'
}: {
  rfxRequestId: string;
  vendorId: string;
  message: string;
  text: string;
  properties?: Property[];
  contentType?: string;
}): RFXQuestion {
  const newProperties = Array.isArray(properties) ? properties : [];
  newProperties.push(createProperty(QuestionPropertyIds.MessageContent, text));

  return {
    rfxQuestionRevisionedId: {
      id: {
        value: `${rfxRequestId}-${Date.now()}-${vendorId}`
      },
      revision: 1
    },
    rfxQuestionVersion: 1,
    rfxRequestRevisionedId: {
      id: { value: rfxRequestId },
      revision: 1
    },
    author: {
      userType: RFXUserType.EXTERNAL,
      id: {
        value: vendorId
      }
    },
    rfxQuestionClassId: {
      value: 'AWSRFIQuestion'
    },
    message,
    contentType,
    createdAt: Date.now(),
    lastUpdatedAt: Date.now(),
    rfxQuestionDocuments: [],
    properties: newProperties
  };
}

/**
 * Create RFI supplier participation question
 */
export function createSupplierRFIParticipationQuestion({
  participationOption,
  rfxRequestId,
  vendorId
}: {
  participationOption: string;
  rfxRequestId: string;
  vendorId: string;
}): RFXQuestion {
  const participationProperties = [
    {
      id: {
        value: QuestionPropertyIds.ParticipationConfirmation
      },
      value: {
        valueString: {
          value: participationOption
        },
        dataType: {
          value: 'Boolean'
        }
      }
    }
  ];
  return createSupplierRFIQuestion({
    rfxRequestId,
    vendorId,
    message: '',
    text: '',
    properties: participationProperties
  });
}

/**
 * Creates a Document object for a file attachment
 * @param filename - Name of the uploaded file
 * @param stagingArn - AWS ARN of the staged file
 * @returns Document object conforming to the Document interface
 */
export function createQADocument(filename: string, stagingArn: string): Document {
  return {
    documentType: RFXDocumentType.QA,
    attachment: {
      externalId: uuid() + '-' + filename,
      filename,
      description: '',
      stagingS3Uri: getS3UriFromS3Arn(stagingArn)
    }
  };
}

/**
 * Updates the questions array by adding new documents to the specified RFX question
 * @param questions - Current array of RFX questions
 * @param rfxQuestion - The specific question to update
 * @param newDocuments - Array of new documents to add to the question
 * @returns Updated array of RFX questions
 */
export function updateQuestionsWithAttachments(
  questions: RFXQuestion[],
  rfxQuestion: RFXQuestion,
  newDocuments: Document[]
): RFXQuestion[] {
  return questions.map((q) => {
    if (q.rfxQuestionRevisionedId.id.value === rfxQuestion.rfxQuestionRevisionedId.id.value) {
      return {
        ...q,
        rfxQuestionDocuments: [...q.rfxQuestionDocuments, ...newDocuments]
      };
    }
    return q;
  });
}

/**
 * Sort questions by createAt
 * @param questions questions to sort
 * @returns newly created sorted questions
 */
export function sortQuestionsByCreationTime(questions: RFXQuestion[]): RFXQuestion[] {
  return [...questions].sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Creates a map of RFX questions grouped by vendor ID.
 *
 * @param questions - Array of RFXQuestion objects to be processed
 * @returns A Map where keys are vendor IDs and values are arrays of corresponding RFXQuestions
 *
 * @description
 * This function processes an array of RFXQuestion objects and groups them by vendor ID.
 * For questions with EXTERNAL authors, the vendor ID is taken from the author's ID.
 * For questions with INTERNAL authors, the vendor ID is taken from the MessageTo property.
 *
 * @example
 * ```typescript
 * const questions: RFXQuestion[] = [...];
 * const questionMap = createVendorQuestionMap(questions);
 *
 * // Access questions for a specific vendor
 * const vendorQuestions = _.get(questionMap, 'VENDOR123', []);
 * ```
 */
export function createVendorQuestionMap(questions: RFXQuestion[] | undefined): Record<string, RFXQuestion[]> {
  const vendorMap: Record<string, RFXQuestion[]> = {};

  if (!Array.isArray(questions)) {
    return vendorMap;
  }

  questions.forEach((question) => {
    let vendorId: string | undefined;

    if (question.author.userType === RFXUserType.EXTERNAL) {
      // For EXTERNAL users, use the author's ID
      vendorId = question.author.id.value;
    } else if (question.author.userType === RFXUserType.INTERNAL) {
      // For INTERNAL users, look for MessageTo property
      const messageToProp = question.properties?.find((prop) => prop.id.value === 'MessageTo');
      if (messageToProp?.value?.valueString?.value) {
        vendorId = messageToProp.value.valueString.value;
      }
    }

    if (vendorId) {
      // If the vendor already exists in the map, append to their array
      // If not, create a new array
      const existingQuestions = _.get(vendorMap, vendorId, []);
      vendorMap[vendorId] = [...existingQuestions, question];
    }
  });

  return vendorMap;
}

/**
 * Generates an array of vendor status information based on RFX questions and request data.
 *
 * @param questionMap - Map of vendor IDs to their corresponding RFXQuestion arrays
 * @param rfxRequest - The RFXRequest object containing supplier information
 * @returns Array of VendorStatus objects containing status information for each vendor
 *
 * @description
 * This function determines the participation status of each vendor based on the following rules:
 * - If a vendor is not in the suppliers list, status is 'pending'
 * - If a vendor has a ParticipationConfirmation property:
 *   - Value 'true' sets status to 'participating'
 *   - Value 'false' sets status to 'declined'
 * - If a supplier has no questions, they are added with 'pending' status
 *
 * The lastUpdatedAt timestamp is taken from the most recent question for each vendor.
 *
 * @example
 * ```typescript
 * const questionMap = createVendorQuestionMap(rfxQuestions);
 * const vendorStatuses = generateVendorStatusArray(questionMap, rfxRequest);
 *
 * // Example output:
 * // [
 * //   {
 * //     vendorId: 'VENDOR123',
 * //     lastUpdatedAt: '1736289801000',
 * //     status: 'participating'
 * //   },
 * //   ...
 * // ]
 * ```
 *
 * @throws Will not throw errors but may return empty arrays if input data is invalid
 */
export function generateVendorStatusArray(
  questionMap: Record<string, RFXQuestion[]>,
  rfxRequest: RFXRequest
): VendorStatus[] {
  const result: VendorStatus[] = [];
  const supplierIds = new Set(rfxRequest.suppliers.map((supplier) => supplier.supplierId.value));

  // Process each vendor in the question map
  _.forEach(questionMap, (questions, vendorId) => {
    // Sort questions by lastUpdatedAt in descending order to get the most recent first
    const sortedQuestions: RFXQuestion[] = [...questions].sort(
      (a, b) => (b.lastUpdatedAt || b.createdAt) - (a.lastUpdatedAt || a.createdAt)
    );

    // Find the most recent question with ParticipationConfirmation property
    const participationQuestion: RFXQuestion | undefined = sortedQuestions.find((question) =>
      question.properties?.some((prop) => prop.id.value === 'ParticipationConfirmation')
    );

    let status: VendorParticipationStatus = VendorParticipationStatus.PENDING;

    // If vendor is not in suppliers list, status remains pending
    if (supplierIds.has(vendorId)) {
      if (participationQuestion) {
        const participationProp = participationQuestion.properties?.find(
          (prop) => prop.id.value === 'ParticipationConfirmation'
        );

        const participationValue = participationProp?.value?.valueString?.value;
        status =
          participationValue === 'true' ? VendorParticipationStatus.PARTICIPATING : VendorParticipationStatus.DECLINED;
      }
    }

    // Use the most recent question's timestamp
    const lastUpdatedAt: number = sortedQuestions[0]?.lastUpdatedAt || sortedQuestions[0]?.createdAt;

    result.push({
      vendorId,
      lastUpdatedAt: lastUpdatedAt.toString(),
      status
    });
  });

  // Add any suppliers that don't have questions as pending
  rfxRequest.suppliers.forEach((supplier) => {
    if (!_.has(questionMap, supplier.supplierId.value)) {
      result.push({
        vendorId: supplier.supplierId.value,
        lastUpdatedAt: '',
        status: VendorParticipationStatus.PENDING
      });
    }
  });

  return result;
}

/**
 * Check whether all the pending qestion and documents are already being processed.
 *
 * @param question pending question
 * @param documents pending documents
 * @param allQuestions reloaded all questions, they are used to check pending question and documents
 * @returns {boolean} whether no pending question and documents in reloaded all questions
 */
export function hasNoPendingQuestionAndDocuments({
  question,
  documents,
  allQuestions
}: {
  question: RFXQuestion;
  documents: CreateRFXQuestionAttachmentOutput[];
  allQuestions: RFXQuestion[];
}): boolean {
  const pendingQuestionId = question.rfxQuestionRevisionedId;
  const foundQuestion = allQuestions.find((q) => _.isEqual(q.rfxQuestionRevisionedId, pendingQuestionId));

  if (foundQuestion === undefined) {
    // Cannot find pending question in the loaded questions
    return false;
  }

  if (_.isEmpty(documents)) {
    // Pending question has been processed, and no need to check pending documents
    return true;
  }

  if (_.isEmpty(foundQuestion.rfxQuestionDocuments)) {
    // pending question has documents, but has not being attached yet.
    return false;
  }

  const pendingExternalIds = documents.map((doc) => doc.externalId);
  const foundExternalIds = foundQuestion.rfxQuestionDocuments.map((doc) => doc.attachment.externalId);
  // Whether all pending external IDs has been found
  return _.isEmpty(_.difference(pendingExternalIds, foundExternalIds));
}

/**
 * Filters an array of RFX questions to exclude buyer reply-all messages
 * 
 * @param questions Array of RFX questions to filter
 * @returns Filtered array of non reply-all questions (suppliers correspondence and participation selection)
 */
export const getNonReplyAllQuestions = (questions: RFXQuestion[]): RFXQuestion[] => {
  return questions.filter(question =>
    extractPropertyOrElseDefault(question.properties, 'MessageTo', '') !== 'ALL_USER'
  );
};

/**
 * Filters an array of RFX questions to include only those specifically addressed to a given receiver.
 *
 * @param questions - Array of RFXQuestion objects to filter
 * @param receiver - The ID of the receiver to filter questions for
 * @returns An array of RFXQuestion objects that have their MessageTo property matching the specified receiver
 */
export const getQuestionsForReceiver = (questions: RFXQuestion[], receiver: string): RFXQuestion[] => {
  return questions.filter(question =>
    extractPropertyOrElseDefault(question.properties, 'MessageTo', '') === receiver
  );
};